import React, { useState, useEffect, useRef } from "react";
import CatalogCard from "./CatalogCard";
import "./Catalog.css";

export default function Catalog({ catalogProducts, openModal }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const carouselRef = useRef(null);
  
  const visibleCards = 3; // Número de tarjetas visibles en móvil
  const totalProducts = catalogProducts.length;
  
  const nextSlide = () => {
    if (isAnimating || currentIndex >= totalProducts - visibleCards) return;
    
    setIsAnimating(true);
    setCurrentIndex(prev => Math.min(prev + 1, totalProducts - visibleCards));
    
    setTimeout(() => setIsAnimating(false), 300);
  };
  
  const prevSlide = () => {
    if (isAnimating || currentIndex <= 0) return;
    
    setIsAnimating(true);
    setCurrentIndex(prev => Math.max(prev - 1, 0));
    
    setTimeout(() => setIsAnimating(false), 300);
  };
  
  const goToSlide = (index) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex(index);
    
    setTimeout(() => setIsAnimating(false), 300);
  };
  
  // Efecto para arrastrar con ratón/touch
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    
    const handleMouseDown = (e) => {
      isDragging = true;
      startX = e.pageX - carousel.offsetLeft;
      scrollLeft = currentIndex;
    };
    
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 0.5; // Velocidad de arrastre
      
      if (walk > 50 && currentIndex > 0) {
        prevSlide();
        isDragging = false;
      } else if (walk < -50 && currentIndex < totalProducts - visibleCards) {
        nextSlide();
        isDragging = false;
      }
    };
    
    const handleMouseUp = () => {
      isDragging = false;
    };
    
    carousel.addEventListener('mousedown', handleMouseDown);
    carousel.addEventListener('mousemove', handleMouseMove);
    carousel.addEventListener('mouseup', handleMouseUp);
    carousel.addEventListener('mouseleave', handleMouseUp);
    
    // Touch events para móviles
    carousel.addEventListener('touchstart', (e) => {
      isDragging = true;
      startX = e.touches[0].pageX - carousel.offsetLeft;
      scrollLeft = currentIndex;
    });
    
    carousel.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const x = e.touches[0].pageX - carousel.offsetLeft;
      const walk = (x - startX) * 0.5;
      
      if (walk > 50 && currentIndex > 0) {
        prevSlide();
        isDragging = false;
      } else if (walk < -50 && currentIndex < totalProducts - visibleCards) {
        nextSlide();
        isDragging = false;
      }
    });
    
    carousel.addEventListener('touchend', handleMouseUp);
    
    return () => {
      carousel.removeEventListener('mousedown', handleMouseDown);
      carousel.removeEventListener('mousemove', handleMouseMove);
      carousel.removeEventListener('mouseup', handleMouseUp);
      carousel.removeEventListener('mouseleave', handleMouseUp);
      carousel.removeEventListener('touchstart', handleMouseDown);
      carousel.removeEventListener('touchmove', handleMouseMove);
      carousel.removeEventListener('touchend', handleMouseUp);
    };
  }, [currentIndex, totalProducts, visibleCards]);
  
  // Indicadores de navegación
  const indicators = Array.from(
    { length: Math.max(1, totalProducts - visibleCards + 1) }, 
    (_, i) => i
  );

  return (
    <section className="catalog" id="catalogo">
      <div className="catalog-logo-container">
        <img src="/images/icons/catalog-logo.webp" alt="Catálogo" className="catalog-logo" />
      </div>
      
      <div className="catalog-carousel-wrapper">
        {/* Botón anterior */}
        <button 
          className={`carousel-btn carousel-btn-prev ${currentIndex === 0 ? 'disabled' : ''}`}
          onClick={prevSlide}
          aria-label="Producto anterior"
          disabled={currentIndex === 0 || isAnimating}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        
        {/* Contenedor del carrusel */}
        <div className="catalog-carousel-container" ref={carouselRef}>
          <div 
            className="catalog-carousel-track"
            style={{
              transform: `translateX(calc(-${currentIndex * (100 / visibleCards)}% - ${currentIndex * 0.5}rem))`,
              transition: isAnimating ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
            }}
          >
            {catalogProducts.map((product) => (
              <div 
                key={product.id} 
                className="catalog-carousel-slide"
                style={{ flex: `0 0 calc(${100 / visibleCards}% - 0.5rem)` }}
              >
                <CatalogCard 
                  product={product} 
                  openModal={openModal}
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* Botón siguiente */}
        <button 
          className={`carousel-btn carousel-btn-next ${currentIndex >= totalProducts - visibleCards ? 'disabled' : ''}`}
          onClick={nextSlide}
          aria-label="Producto siguiente"
          disabled={currentIndex >= totalProducts - visibleCards || isAnimating}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
      
      {/* Indicadores de posición */}
      {indicators.length > 1 && (
        <div className="carousel-indicators">
          {indicators.map((index) => (
            <button
              key={index}
              className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Ir a diapositiva ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}